import Papa from 'papaparse';

import { PromoCodeGroupFileType, PromoCodeGroupProcessingStatus, User_Role } from '@occc/graphql';

import { PromoCodeGroupFormValues } from './promocodes-configuration.types';

import { messages } from '../../promocodes.messages';

const CSV_VALIDATION_PREVIEW_ROWS = 5;

export const validatePromoCodesCsvFile = (file: File): Promise<boolean> =>
  new Promise((resolve) => {
    Papa.parse<Record<string, unknown>>(file, {
      header: true,
      delimiter: ',',
      preview: CSV_VALIDATION_PREVIEW_ROWS,
      skipEmptyLines: true,
      complete: (results) => {
        const fields = results.meta.fields ?? [];
        const hasExactlyOneColumn = fields.length === 1 && Boolean(fields[0]?.trim());
        const hasExtraColumns = results.data.some(
          (row) => row && Object.prototype.hasOwnProperty.call(row, '__parsed_extra'),
        );
        resolve(hasExactlyOneColumn && !hasExtraColumns && !results.errors.length);
      },
      error: () => resolve(false),
    });
  });

export const isPromoCodeGroupProcessingFinished = (group: PromoCodeGroupFormValues) =>
  group.processingStatus === PromoCodeGroupProcessingStatus.Completed ||
  group.processingStatus === PromoCodeGroupProcessingStatus.Failed;

export const hasUnfinishedPromoCodeGroups = (groups: PromoCodeGroupFormValues[]) =>
  groups.some((group) => !isPromoCodeGroupProcessingFinished(group));

export type PromoCodeGroupDownloadMenuItem = {
  label: string;
  fileType: PromoCodeGroupFileType;
};

export const canDownloadPromoCodeGroupFiles = (userRole?: User_Role | null) =>
  userRole === User_Role.Admin ||
  userRole === User_Role.Support ||
  userRole === User_Role.AdminReadOnly;

export const isPromoCodeGroupDownloadAvailable = (group: PromoCodeGroupFormValues) =>
  isPromoCodeGroupProcessingFinished(group);

export const getPromoCodeGroupDownloadMenuItems = (
  group: PromoCodeGroupFormValues,
): PromoCodeGroupDownloadMenuItem[] => {
  if (!isPromoCodeGroupDownloadAvailable(group)) {
    return [];
  }

  return [
    {
      label: messages.downloadProcessedCodes,
      fileType: PromoCodeGroupFileType.Processed,
    },
  ];
};

export const getPromoCodeGroupStatusLabel = (status: string) => {
  const labels: Record<PromoCodeGroupProcessingStatus, string> = {
    [PromoCodeGroupProcessingStatus.Pending]: 'Pending',
    [PromoCodeGroupProcessingStatus.InProgress]: 'In Progress...',
    [PromoCodeGroupProcessingStatus.Completed]: 'Completed',
    [PromoCodeGroupProcessingStatus.Failed]: 'Failed',
  };

  return labels[status as PromoCodeGroupProcessingStatus] ?? status;
};

export const getPromoCodeGroupUploadCounts = (group: PromoCodeGroupFormValues) => {
  if (!isPromoCodeGroupProcessingFinished(group)) {
    return {
      codesSuccessfullyUploaded: null,
      codesUploadFailed: null,
    };
  }

  return {
    codesSuccessfullyUploaded: group.codesStats.createdCount,
    codesUploadFailed: group.codesStats.failedCount,
  };
};
